import { useEffect, useMemo, useState } from 'react';
import { hostPayouts } from '../api/host';
import { useCurrency } from '../components/CurrencyContext';

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ON_HOLD', label: 'On hold' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'PAID', label: 'Paid' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const badgeStyles = {
  PENDING: 'bg-amber-50 text-amber-700 ring-amber-100',
  ON_HOLD: 'bg-rose-50 text-rose-700 ring-rose-100',
  SCHEDULED: 'bg-sky-50 text-sky-700 ring-sky-100',
  PROCESSING: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-100',
  CANCELLED: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const statusLabel = (value) => String(value || 'unknown').replace(/_/g, ' ').toLowerCase();

const PayoutBadge = ({ status }) => {
  const key = String(status || '').toUpperCase();
  const cls = badgeStyles[key] || 'bg-slate-100 text-slate-700 ring-slate-200';
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${cls}`}>{statusLabel(key)}</span>;
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const HostPayouts = ({ accessToken }) => {
  const { formatCurrency } = useCurrency();
  const [filters, setFilters] = useState({ status: '', from: '', to: '' });
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const formatMoney = (currency, amount) => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return 'N/A';
    const value = numeric / 100;
    return formatCurrency(value, currency || 'INR') || `${currency || 'INR'} ${value}`;
  };
  const resolveAmount = (row) => (row?.amountPaise ?? row?.amount);

  const load = async (page = pagination.page || 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await hostPayouts(accessToken, { ...filters, page, limit: pagination.limit });
      setItems(res.items || []);
      setPagination({ page: res.page || page, limit: res.limit || pagination.limit, total: res.total || 0 });
    } catch (err) {
      setError(err.message || 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, filters.status]);

  const applyFilters = () => {
    load(1);
  };

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 20)));
  }, [pagination]);

  return (
    <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Payouts</p>
          <h1 className="text-xl font-bold text-slate-900">Host payouts</h1>
          <p className="text-sm text-slate-600">Track when payouts are scheduled and paid.</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <select
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={filters.from}
          onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.to}
          onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={applyFilters}
          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        >
          Apply filters
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Payout</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Scheduled</th>
              <th className="px-4 py-3">Paid</th>
              <th className="px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {error && !loading && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-rose-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !items.length && !error && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-slate-500">
                  No payouts found.
                </td>
              </tr>
            )}
            {items.map((payout) => {
              const payoutRef = payout.payoutRef || payout.reference || `#${payout.id}`;
              const payoutCreatedAt = payout.createdAt || payout.created_at;
              const payoutScheduledFor = payout.scheduledFor || payout.scheduled_for;
              const payoutPaidAt = payout.paidAt || payout.paid_at;
              const paymentRef = payout.paymentId || payout.payment_id;
              return (
                <tr key={payout.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{payoutRef}</div>
                  <div className="text-xs text-slate-500">{paymentRef ? `Payment ${paymentRef}` : 'Manual payout'}</div>
                </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{formatMoney(payout.currency, resolveAmount(payout))}</td>
                  <td className="px-4 py-3">
                    <PayoutBadge status={payout.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{formatDateTime(payoutScheduledFor)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{formatDateTime(payoutPaidAt)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{formatDateTime(payoutCreatedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <div>
          Page {pagination.page || 1} of {pageCount}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => load(Math.max(1, (pagination.page || 1) - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) <= 1}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => load((pagination.page || 1) + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) >= pageCount}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default HostPayouts;
